/**
 * Safe JSON.parse wrapper that never throws.
 * Returns a { value, error } result shape so callers can handle failures gracefully.
 *
 * @param {string} input - The string to parse.
 * @param {Object} [options] - Options.
 * @param {*} [options.fallback=undefined] - Value returned inside `value` when parsing fails.
 * @param {string} [options.module] - Module/component name for diagnostics.
 * @param {string} [options.context] - Additional context (e.g. actionId, event type).
 * @returns {{ value: *, error: Error|null }}
 */
export const safeParseJson = (input, { fallback = undefined, module: moduleName, context } = {}) => {
  if (input === null || input === undefined || input === '') {
    return { value: fallback, error: null };
  }

  if (typeof input === 'object') {
    return { value: input, error: null };
  }

  try {
    return { value: JSON.parse(input), error: null };
  } catch (error) {
    if (moduleName) {
      const preview = typeof input === 'string' ? input.slice(0, 120) : String(input).slice(0, 120);
      console.warn(`[safeParseJson] Parse failure in ${moduleName}${context ? ` (${context})` : ''}: ${error.message} | input preview: ${preview}`);
    }

    return { value: fallback, error };
  }
};

/**
 * Convenience wrapper that returns the parsed value directly (or the fallback).
 * Use when you only need the value and want to silently discard parse errors.
 *
 * @param {string} input - The string to parse.
 * @param {*} [fallback=undefined] - Value returned when parsing fails.
 * @returns {*}
 */
export const safeParseJsonValue = (input, fallback = undefined) => {
  if (input === null || input === undefined || input === '') {
    return fallback;
  }

  if (typeof input === 'object') {
    return input;
  }

  try {
    return JSON.parse(input);
  } catch {
    return fallback;
  }
};
